import DataTableCellCompanyEmployeeEmail from "@/components/data-table-company-employees/data-table-cell-company-employee-email";
import DataTableCellCompanyEmployeePhone from "@/components/data-table-company-employees/data-table-cell-company-employee-phone";
import { getLevelLabel } from "@/lib/employee-level";
import EmployeeIntegrationsSync from "@/components/integrations/employee/employee-integrations-sync";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import { EmployeeDTO } from "@shaarp/common";
import { Mail, Phone } from "lucide-react";
import { RiLinkedinFill } from "react-icons/ri";
import { Link } from "react-router-dom";

export function EmployeeCard({
  employee,
  companyId,
  onEmployeeChanged,
  compact = false,
}: {
  employee: EmployeeDTO;
  companyId: string;
  onEmployeeChanged: (update: Partial<EmployeeDTO> & { id: string }) => void;
  compact?: boolean;
}) {
  const { t, currentLanguage } = useTranslation();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { teamSelected } = useTeams();

  const showEmail = teamSelected?.featureContactRevealEmailEnabled ?? true;
  const showPhone = teamSelected?.featureContactRevealPhoneEnabled ?? true;

  const initials =
    `${employee.firstName?.charAt(0) || ""}${employee.lastName?.charAt(0) || ""}`.toUpperCase();

  const levelLabel = getLevelLabel(employee.level, t);

  const tenureLabel = employee.employedFrom
    ? `+${convertDateToDistanceNow(employee.employedFrom, {
        addSuffix: false,
        languageOverride: currentLanguage,
      })}`
    : null;

  const emailUnavailableLabel = (() => {
    if (employee.isBlacklisted) {
      return t("components.dataTableCompanyEmployees.email.notAvailable");
    }
    if (employee.emailStatus === "UNAVAILABLE") {
      return t("components.dataTableCompanyEmployees.email.notFound");
    }
    if (
      employee.emailStatus !== "VERIFIED" &&
      employee.emailStatus !== "UNKNOWN"
    ) {
      return t("components.dataTableCompanyEmployees.email.notAvailable");
    }
    return null;
  })();

  const phoneUnavailableLabel = (() => {
    if (employee.isBlacklisted || employee.phoneStatus === "SIGNAL_WRONG") {
      return t("components.dataTableCompanyEmployees.phone.notAvailable");
    }
    if (employee.phoneStatus === "NOT_FOUND") {
      return t("components.dataTableCompanyEmployees.phone.notFound");
    }
    return null;
  })();

  return (
    <div className="flex flex-col rounded-xl border p-5 gap-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-12 h-12 shrink-0">
            <AvatarImage
              src={employee.photoUrl || ""}
              alt={employee.firstName || ""}
            />
            <AvatarFallback>{initials || "?"}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold truncate">
              {employee.firstName} {employee.lastName}
            </span>
            <span className="text-sm text-muted-foreground truncate">
              {employee.jobTitle}
            </span>
          </div>
        </div>
        {employee.linkedInUrl && (
          <Button size="icon" variant="ghost" className="shrink-0" asChild>
            <Link to={employee.linkedInUrl} target="_blank">
              <RiLinkedinFill className="w-4 h-4" />
            </Link>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {levelLabel && <Badge variant="secondary">{levelLabel}</Badge>}
        {tenureLabel && <Badge variant="secondary">{tenureLabel}</Badge>}
        {employee.country && (
          <Badge variant="secondary">{employee.country}</Badge>
        )}
      </div>

      <div className="flex flex-col gap-2 mt-auto">
        {showEmail && (
          <div className="min-w-0 [&>button]:w-full [&>div]:max-w-full [&>div>button:first-child]:min-w-0 [&>div>button:first-child]:flex-1 [&>div>button:first-child]:overflow-hidden [&>div>button:first-child]:truncate [&>div>button:first-child]:justify-start">
            {emailUnavailableLabel ? (
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-1">
                <Mail className="w-4 h-4" />
                {emailUnavailableLabel}
              </p>
            ) : (
              <DataTableCellCompanyEmployeeEmail
                companyId={companyId}
                employee={employee}
                onEmailRevealed={onEmployeeChanged}
                compact={compact}
              />
            )}
          </div>
        )}
        {showPhone && (
          <div className="min-w-0 [&>button]:w-full [&>div]:max-w-full [&>div>button:first-child]:min-w-0 [&>div>button:first-child]:flex-1 [&>div>button:first-child]:overflow-hidden [&>div>button:first-child]:truncate [&>div>button:first-child]:justify-start">
            {phoneUnavailableLabel ? (
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-1">
                <Phone className="w-4 h-4" />
                {phoneUnavailableLabel}
              </p>
            ) : (
              <DataTableCellCompanyEmployeePhone
                employee={employee}
                companyId={companyId}
                onPhoneRevealed={onEmployeeChanged}
                compact={compact}
              />
            )}
          </div>
        )}
        {connectedCrmIntegrations.length > 0 && (
          <EmployeeIntegrationsSync
            employee={employee}
            companyId={companyId}
            onSynced={onEmployeeChanged}
            showLabel
          />
        )}
      </div>
    </div>
  );
}

export function EmployeeCardSkeleton() {
  return (
    <div className="flex flex-col rounded-xl border p-5 gap-2">
      <Skeleton variant="subtle" className="h-2 w-20" />
      <Skeleton variant="subtle" className="h-1.5 w-14" />
    </div>
  );
}
